<template>
  <v-container fluid class="pa-2">

    <v-layout row wrap align-center class="mb-2">
      <v-flex xs12 sm4 md3>
        <v-menu
          ref="menuFrom"
          v-model="menuFrom"
          :close-on-content-click="false"
          :nudge-right="40"
          :return-value.sync="dateFrom"
          lazy
          transition="scale-transition"
          offset-y
          full-width
          max-width="290px"
          min-width="290px"
        >
          <v-text-field
            slot="activator"
            v-model="dateFromFormatted"
            label="Start Date"
            prepend-icon="mdi-calendar"
            readonly
          />
          <v-date-picker
            v-model="dateFrom"
            no-title
            min="2020-01-01"
            @input="$refs.menuFrom.save(dateFrom)"
          />
        </v-menu>
      </v-flex>

      <v-flex xs12 sm4 md3>
        <v-menu
          ref="menuTo"
          v-model="menuTo"
          :close-on-content-click="false"
          :nudge-right="40"
          :return-value.sync="dateTo"
          lazy
          transition="scale-transition"
          offset-y
          full-width
          max-width="290px"
          min-width="290px"
        >
          <v-text-field
            slot="activator"
            v-model="dateToFormatted"
            label="End Date"
            prepend-icon="mdi-calendar"
            readonly
          />
          <v-date-picker
            v-model="dateTo"
            no-title
            min="2020-01-01"
            @input="$refs.menuTo.save(dateTo)"
          />
        </v-menu>
      </v-flex>

      <v-flex xs12 sm4 md6 class="text-xs-right">
        <v-progress-circular v-if="loading" indeterminate color="primary" class="mr-3" />
        <v-btn class="downlink" flat @click="copyCalendarLink">
          <v-icon left color="dark_grey" >mdi-link-variant</v-icon> Copy Shared Calendar Link
        </v-btn>
        <v-btn class="downlink" flat :loading="exporting" @click="downloadExport">
          <v-icon left color="dark_grey">mdi-cloud-download</v-icon> {{ downloadLabel }}
        </v-btn>
      </v-flex>
    </v-layout>

    <v-snackbar v-model="linkCopied" :timeout="2500" bottom>
      Calendar link copied to clipboard
    </v-snackbar>

    <div class="table-scroll-container">
      <table class="store-table">

        <thead>
          <tr>
            <th class="col-store col-corner">
              <div class="store-header-labels">
                <span>Store</span>
              </div>
            </th>
            <th
              v-for="day in calendarDays"
              :key="day.dateStr"
              class="col-day"
              :class="{ 'week-start': day.dow === 0 }"
            >
              <div class="day-number">{{ day.day }}</div>
              <div class="day-dow">{{ dowLabels[day.dow] }}</div>
            </th>
          </tr>
        </thead>

        <tbody>
          <tr v-for="store in stores" :key="store.costco_id">
            <td class="col-store store-info-cell">
              <span class="store-cluster">{{ store.cluster || '-' }}</span>
              <span class="store-whid">#{{ store.warehouse_id }}</span>
              <span class="store-city">{{ store.city }}</span>
            </td>
            <td
              v-for="day in calendarDays"
              :key="day.dateStr"
              class="col-day day-cell"
              :class="{
                'week-start': day.dow === 0,
                'empty-cell': cellEvents(store.costco_id, day.dateStr).length === 0
              }"
            >
              <div
                v-for="(event, i) in cellEvents(store.costco_id, day.dateStr)"
                :key="i"
                class="event-pill"
                :class="{ 'event-boosted': event.boosted }"
                @click="openEventDialog(event)"
              >
                <span class="event-name">{{ chipLabel(event) }}</span>
                <span v-if="event.boosted" class="boosted-badge">Boosted</span>
              </div>
            </td>
          </tr>
        </tbody>

      </table>
    </div>

    <!-- Event detail dialog -->
    <v-dialog v-model="eventDialogOpen" max-width="480" scrollable>
      <v-card v-if="selectedEvent">

        <v-card-title class="headline pb-1">
          {{ chipLabel(selectedEvent) }}
          <v-chip v-if="selectedEvent.boosted" small color="#6200ea" text-color="white" class="ml-2">Boosted</v-chip>
          <v-btn class="btn-close" icon small @click="eventDialogOpen = false">
            <v-icon small>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-divider />

        <v-card-text>
          <div class="dialog-section-label">Store</div>
          <div class="dialog-field">{{ selectedEvent.storename }}</div>
          <div class="dialog-field">{{ selectedEvent.address }}, {{ selectedEvent.city }}, {{ selectedEvent.state }} {{ selectedEvent.zipcode }}</div>
          <div class="dialog-field">Store #{{ selectedEvent.warehouse_id }}</div>
          <div class="dialog-field">{{ formatTime(selectedEvent.time_start) }} - {{ formatTime(selectedEvent.time_end) }}</div>

          <v-divider class="my-3" />

          <div class="dialog-section-label">Products</div>
          <div v-if="selectedEvent.wines && selectedEvent.wines.length">
            <div v-for="(wine, i) in selectedEvent.wines" :key="i" class="dialog-field">
              {{ wine }}
            </div>
          </div>
          <div v-else class="dialog-field dialog-empty">No products listed</div>

          <template v-if="selectedEvent.sub_wines && selectedEvent.sub_wines.length">
            <div class="dialog-section-label mt-3">Substitutions</div>
            <div v-for="(wine, i) in selectedEvent.sub_wines" :key="i" class="dialog-field">
              {{ wine }}
            </div>
          </template>
        </v-card-text>

        <v-divider />
        <v-card-actions>
          <v-spacer />
          <v-btn flat @click="eventDialogOpen = false">Close</v-btn>
        </v-card-actions>

      </v-card>
    </v-dialog>

  </v-container>
</template>

<script>
import axios from 'axios'
import { TokenService } from '../services/storage.service'
import { downloadCombinedExport } from '../utils/combinedExport'

export default {
  data: () => ({
    dateFrom: localStorage.getItem('retailDateFrom') ||
      new Date().toISOString().substr(0, 7) + '-01',
    dateTo: localStorage.getItem('retailDateTo') ||
      new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).toLocaleDateString('sv'),
    menuFrom: false,
    menuTo: false,
    loading: false,
    stores: [],
    events: {},
    dowLabels: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
    eventDialogOpen: false,
    selectedEvent: null,
    linkCopied: false,
    exporting: false,
  }),

  computed: {
    authenticated () {
      return this.$store.state.accessKey != null && this.$store.state.authenticating == false
    },

    // Read reactively from the store so the label updates after set_retail
    // commits on the same page load; fall back to localStorage on reload
    itemListView () {
      const retail = this.$store.state.retail
      if (retail && retail.show_item_list != null) {
        return retail.show_item_list
      }
      return TokenService.getItemListView()
    },

    downloadLabel () {
      return this.itemListView == 1 ? 'Download Calendar/Item List' : 'Download Calendar'
    },

    dateFromFormatted () {
      return this.formatDate(this.dateFrom)
    },

    dateToFormatted () {
      return this.formatDate(this.dateTo)
    },

    calendarDays () {
      const days = []
      const start = new Date(this.dateFrom + 'T00:00:00')
      const end = new Date(this.dateTo + 'T00:00:00')
      for (const d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        // Label the first column and each 1st of a month with the month name,
        // since a range can span multiple months
        const showMonth = days.length === 0 || d.getDate() === 1
        days.push({
          day: showMonth
            ? d.toLocaleString('default', { month: 'short' }) + ' ' + d.getDate()
            : d.getDate(),
          dateStr: d.toLocaleDateString('sv'),
          dow: d.getDay()
        })
      }
      return days
    },

    eventMap () {
      const map = {}
      for (const [date, events] of Object.entries(this.events)) {
        for (const event of events) {
          if (!map[event.costco_id]) map[event.costco_id] = {}
          if (!map[event.costco_id][date]) map[event.costco_id][date] = []
          map[event.costco_id][date].push(event)
        }
      }
      return map
    },

    woDesignators () {
      // For client names with >1 distinct work order this month, map work_order → ' -1', ' -2', etc.
      const seen = new Set()
      const clientWOs = {}
      for (const date of Object.keys(this.events).sort()) {
        for (const event of this.events[date]) {
          const wo = event.work_order
          const name = this.cleanClientName(event.client_name)
          if (wo && !seen.has(wo)) {
            seen.add(wo)
            if (!clientWOs[name]) clientWOs[name] = []
            clientWOs[name].push(wo)
          }
        }
      }
      const map = {}
      for (const wos of Object.values(clientWOs)) {
        if (wos.length > 1) {
          wos.forEach((wo, i) => { map[wo] = ` -${i + 1}` })
        }
      }
      return map
    },
  },

  watch: {
    authenticated (val) {
      if (val) this.fetchCalendar()
    },

    dateFrom (val) {
      localStorage.setItem('retailDateFrom', val)
      this.fetchCalendar()
    },

    dateTo (val) {
      localStorage.setItem('retailDateTo', val)
      this.fetchCalendar()
    },
  },

  created () {
    if (this.authenticated) this.fetchCalendar()
  },

  methods: {
    formatDate (date) {
      if (!date) return null
      const [year, month, day] = date.split('-')
      return `${month}/${day}/${year}`
    },

    async fetchCalendar () {
      this.loading = true
      const params = new URLSearchParams({
        access_key: TokenService.getAccessKey(),
        date_start: this.dateFrom,
        date_end: this.dateTo
      })
      try {
        const response = await axios.get(
          `${this.$store.state.apiRoot}retail/${TokenService.getLoginCode()}/calendar?${params.toString()}`
        )
        this.stores = response.data.stores || []
        this.events = response.data.events || {}
      } catch (err) {
        console.error('Failed to fetch calendar data:', err)
        this.stores = []
        this.events = {}
      } finally {
        this.loading = false
      }
    },

    cellEvents (costcoId, dateStr) {
      return (this.eventMap[costcoId] || {})[dateStr] || []
    },

    openEventDialog (event) {
      this.selectedEvent = event
      this.eventDialogOpen = true
    },

    copyCalendarLink () {
      // The shared link is the same for everyone: public free/busy view for this retailer
      const link = `${window.location.origin}/shared-calendar/${TokenService.getLoginCode()}`
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(link).then(() => { this.linkCopied = true })
      } else {
        // Clipboard API needs a secure context; fall back to a hidden textarea
        const textarea = document.createElement('textarea')
        textarea.value = link
        document.body.appendChild(textarea)
        textarea.select()
        document.execCommand('copy')
        document.body.removeChild(textarea)
        this.linkCopied = true
      }
    },

    async downloadExport () {
      // Builds the combined Calendar / Item List / Store List workbook. The
      // helper fetches the datasets fresh, so this button produces the same
      // file here as it does on the Item List page. Retailers without the
      // itemListView priv get the workbook with no Item List tab.
      this.exporting = true
      try {
        await downloadCombinedExport({
          apiRoot: this.$store.state.apiRoot,
          dateFrom: this.dateFrom,
          dateTo: this.dateTo,
          includeItemList: this.itemListView == 1
        })
      } catch (err) {
        console.error('Failed to build export:', err)
      } finally {
        this.exporting = false
      }
    },

    cleanClientName (clientName) {
      const match = clientName.match(/^3PA - (.+?) - Agency .+$/)
      if (match) return match[1]
      return clientName
    },

    chipLabel (event) {
      return this.cleanClientName(event.client_name) + (this.woDesignators[event.work_order] || '')
    },

    formatTime (timeStr) {
      if (!timeStr) return ''
      const [hourStr, min] = timeStr.split(':')
      let hour = parseInt(hourStr)
      const ampm = hour >= 12 ? 'PM' : 'AM'
      hour = hour % 12 || 12
      return `${hour}:${min} ${ampm}`
    }
  }
}
</script>

<style scoped>
.table-scroll-container {
  overflow: auto;
  max-height: calc(100vh - 200px);
  border: 1px solid #e0e0e0;
  border-radius: 4px;
}

.store-table {
  border-collapse: collapse;
  min-width: max-content;
  font-size: 13px;
}

thead th {
  position: sticky;
  top: 0;
  z-index: 2;
  background: #f5f5f5;
  border-bottom: 2px solid #d0d0d0;
  white-space: nowrap;
  padding: 6px 8px;
  text-align: center;
}

.col-store {
  position: sticky;
  left: 0;
  z-index: 1;
  background: #fff;
  border-right: 2px solid #d0d0d0;
  min-width: 180px;
  max-width: 180px;
}

.col-corner {
  z-index: 3;
  background: #f5f5f5;
}

.col-day {
  min-width: 160px;
  max-width: 160px;
}

.week-start {
  border-left: 3px solid #b0b0b0 !important;
}

.day-number {
  font-size: 14px;
  font-weight: 700;
  color: #333;
}

.day-dow {
  font-size: 11px;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.store-info-cell {
  padding: 4px 8px;
  border-bottom: 1px solid #e5e5e5;
}

.store-header-labels {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 10px;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-weight: 700;
}

.store-cluster { color: #888; font-size: 12px; margin-right: 4px; }
.store-whid    { font-weight: 700; font-size: 13px; margin-right: 4px; }
.store-city    { color: #444; font-size: 13px; }

.day-cell {
  padding: 3px 4px;
  vertical-align: top;
  border-bottom: 1px solid #e5e5e5;
  border-left: 1px solid #e5e5e5;
}

.empty-cell { background: #f7f8fa; }

.event-pill {
  background: #1565C0;
  color: #fff;
  border-radius: 4px;
  padding: 2px 4px;
  margin-bottom: 1px;
  font-size: 13px;
  font-weight: 500;
  display: flex;
  align-items: flex-start;
  gap: 5px;
  cursor: pointer;
}

.event-pill:hover { filter: brightness(1.15); }
.event-boosted { background: #6200ea; }

.event-name {
  flex: 1;
  word-break: break-word;
  line-height: 1.3;
}

.boosted-badge {
  flex-shrink: 0;
  font-size: 10px;
  font-weight: 700;
  background: rgba(255,255,255,0.25);
  border-radius: 3px;
  padding: 0 4px;
  white-space: nowrap;
}

.dialog-section-label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #888;
  margin-bottom: 4px;
}

.dialog-field {
  font-size: 14px;
  color: #333;
  margin-bottom: 3px;
}

.dialog-empty {
  color: #aaa;
  font-style: italic;
}

.btn-close {
  position: absolute;
  top: 0px;
  right: 0px;
}

.downlink, .downlink v-icon {
  font-size: 1rem;
}
</style>
